import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { SpinnerGap, TrendUp, TrendDown, ShoppingCart, Users, UserMinus, MagnifyingGlass, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { PersonLink } from '@/components/PersonLink';
import { NextPinHint } from '@/components/NextPinHint';

const MONTHS = ['','Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const COLORS = ['#0F1829','#4F6DC7','#8DA0E7','#00C853','#FFD600','#FF6B2B','#FF2B2B','#9333EA'];

// ── Tabela "Pins de Negócio" (regra IHTP) ─────────────────────────
const PIN_TABLE = [
  { min: 15, bonus: 20, nivel: 'Vice-President',    pin: 'IHTPVP', viagem: 'Vice-President' },
  { min: 12, bonus: 18, nivel: 'Ambassador',        pin: 'IHTPA',  viagem: 'Ambassador'     },
  { min:  9, bonus: 16, nivel: 'Executive Diamond', pin: 'DEEC',   viagem: 'Diamond Executive' },
  { min:  6, bonus: 14, nivel: 'Diamond',           pin: 'DEC',    viagem: 'Diamond'        },
  { min:  5, bonus: 12, nivel: 'Gold Executive',    pin: 'GEEC',   viagem: 'Executive'      },
  { min:  4, bonus: 10, nivel: 'Silver Executive',  pin: 'SEEC',   viagem: 'Executive'      },
  { min:  3,  bonus: 8, nivel: 'Executive',         pin: 'EEC',    viagem: 'Executive'      },
  { min:  2,  bonus: 6, nivel: 'Gold',              pin: 'GEC',    viagem: 'Top'            },
  { min:  1,  bonus: 4, nivel: 'Silver',            pin: 'SEC',    viagem: 'Top'            },
];

// ── Escala NetPoints pessoais (Tdom_BonusGroup) ────────────────────
const NPTS_TABLE = [
  { range: 'mais de 7500', min: 7501, bonus: 20, nivel: 'TOP' },
  { range: '4201 — 7500',  min: 4201, bonus: 16, nivel: 'IHTP 4★' },
  { range: '1501 — 4200',  min: 1501, bonus: 12, nivel: 'IHTP 3★' },
  { range: '601 — 1500',   min:  601, bonus:  8, nivel: 'IHTP 2★' },
  { range: '150 — 600',    min:  150, bonus:  4, nivel: 'IHTP 1★' },
  { range: 'até 150',      min:    0, bonus:  0, nivel: '—' },
];

// ── Cores e legendas por tier (strip de 12 meses) ───────────────────
const TIER_META = {
  networks:  { bg: 'bg-[#0F1829]',   fg: 'text-white',           label: 'Networks TOP' },
  top_excel: { bg: 'bg-amber-400',   fg: 'text-amber-950',       label: 'TOP EXCEL'    },
  top:       { bg: 'bg-emerald-500', fg: 'text-emerald-50',      label: 'TOP'          },
  npts_high: { bg: 'bg-indigo-400',  fg: 'text-indigo-50',       label: '16% — IHTP 4★'},
  npts_mid:  { bg: 'bg-sky-300',     fg: 'text-sky-900',         label: '8–12% — IHTP 2★/3★' },
  npts_low:  { bg: 'bg-slate-300',   fg: 'text-slate-700',       label: '4% — IHTP 1★' },
  none:      { bg: 'bg-[#F3F4F6]',   fg: 'text-[#9CA3AF]',       label: '0%'           },
};

function PinHistoryStrip({ history }) {
  if (!history || history.length === 0) return null;

  // Mostra do mais antigo → mais recente (mais legível para sequência)
  const ordered = [...history].sort((a, b) =>
    a.ano !== b.ano ? a.ano - b.ano : a.mes - b.mes,
  );

  // ── Deteta sequências consecutivas (≥3) de meses TOP pessoais ──
  // (`is_top` no backend = bónus ≥20% E ≥1500 NPTS em redes não-TOP).
  // Cada célula recebe `excel_pos` ∈ {null, 'start', 'mid', 'end', 'only'}
  // para fechamos um "frame" dourado contíguo sobre o troço.
  const decorated = ordered.map(m => ({ ...m, excel_pos: null }));
  let runStart = -1;
  for (let i = 0; i <= decorated.length; i += 1) {
    const inRun = i < decorated.length && decorated[i].is_top;
    if (inRun) {
      if (runStart === -1) runStart = i;
    } else if (runStart !== -1) {
      const runLen = i - runStart;
      if (runLen >= 3) {
        for (let j = runStart; j < i; j += 1) {
          if (runStart === i - 1) decorated[j].excel_pos = 'only';
          else if (j === runStart) decorated[j].excel_pos = 'start';
          else if (j === i - 1) decorated[j].excel_pos = 'end';
          else decorated[j].excel_pos = 'mid';
        }
      }
      runStart = -1;
    }
  }

  // Agrupa células contíguas TOP EXCEL para envolvê-las num frame dourado.
  // Cada grupo é { isExcel: bool, cells: [...] }.
  const groups = [];
  for (const cell of decorated) {
    const isExcel = cell.excel_pos !== null;
    const last = groups[groups.length - 1];
    if (last && last.isExcel === isExcel) {
      last.cells.push(cell);
    } else {
      groups.push({ isExcel, cells: [cell] });
    }
  }

  // Tiers presentes (para legenda compacta)
  const tiersPresent = Array.from(new Set(ordered.map(m => m.tier)))
    .filter(t => t !== 'none');
  const hasExcelRun = decorated.some(m => m.excel_pos !== null);

  const renderCell = (m) => {
    const meta = TIER_META[m.tier] || TIER_META.none;
    const monthLabel = MONTHS[m.mes];
    const tooltip = [
      `${monthLabel} ${m.ano}`,
      `Bónus pessoal: ${m.bonus_pct}%`,
      m.is_top ? '• TOP pessoal (≥1500 NPTS em redes não-TOP)' : null,
      m.networks_top > 0
        ? `• ${m.networks_top} ${m.networks_top === 1 ? 'network' : 'networks'} TOP`
        : null,
      m.excel_pos ? '• Pertence a sequência TOP EXCEL (≥3 meses)' : null,
      `• ${meta.label}`,
    ].filter(Boolean).join('\n');
    return (
      <div
        key={`hist-${m.ano}-${m.mes}`}
        data-testid={`pin-history-cell-${m.ano}-${m.mes}`}
        data-tier={m.tier}
        data-excel-pos={m.excel_pos || ''}
        title={tooltip}
        className={`flex-1 rounded ${meta.bg} ${meta.fg} px-1 py-1.5 text-center cursor-help transition-transform hover:scale-105`}
        style={{ minWidth: '36px' }}
      >
        <div className="text-[9px] font-semibold uppercase tracking-wider leading-tight opacity-90">
          {monthLabel}
        </div>
        <div className="text-[9px] font-mono leading-tight opacity-70">
          {String(m.ano).slice(-2)}
        </div>
        <div className="text-[10px] font-bold leading-tight mt-0.5">
          {m.bonus_pct}%
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#FAFBFC] border border-[#E5E7EB] rounded-md p-3 mb-4" data-testid="pin-history-strip">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">
          Histórico (últimos 12 meses)
        </p>
        <p className="text-[10px] text-[#9CA3AF] italic">
          Esquerda → direita: mais antigo → mais recente
        </p>
      </div>
      <div className="flex items-stretch gap-1.5">
        {groups.map((g, idx) => (
          g.isExcel ? (
            <div
              key={`group-excel-${idx}`}
              data-testid={`pin-history-excel-frame-${idx}`}
              className="flex items-stretch gap-1.5 rounded-md p-1 bg-amber-50 ring-2 ring-amber-400 shadow-[0_0_0_1px_rgba(245,158,11,0.25)] relative"
              style={{ flex: g.cells.length }}
              title="Sequência TOP EXCEL — 3+ meses consecutivos em TOP"
            >
              <span className="absolute -top-2 left-2 px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[8px] font-bold uppercase tracking-wider leading-none shadow-sm">
                TOP EXCEL · {g.cells.length}m
              </span>
              {g.cells.map(renderCell)}
            </div>
          ) : (
            <React.Fragment key={`group-plain-${idx}`}>
              {g.cells.map(renderCell)}
            </React.Fragment>
          )
        ))}
      </div>
      {(tiersPresent.length > 0 || hasExcelRun) && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[9px] text-[#6B7280]" data-testid="pin-history-legend">
          {hasExcelRun && (
            <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-50 ring-2 ring-amber-400" />
              Sequência TOP EXCEL (≥3 meses)
            </span>
          )}
          {tiersPresent.map(t => {
            const meta = TIER_META[t];
            return (
              <span key={t} className="inline-flex items-center gap-1">
                <span className={`inline-block w-2.5 h-2.5 rounded ${meta.bg}`} />
                {meta.label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}


function DownlinesRenovacao({ data, fmt }) {
  if (!data) return null;
  const rows = data.data || [];
  const threshold = data.threshold || 0;
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-6 text-sm text-[#6B7280]" data-testid="downlines-renov-empty">
        Sem downlines CS/CT directos com data de renovação registada.
      </div>
    );
  }
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('pt-PT') : '—';
  // Cores conforme proximidade da renovação + estado da meta
  const rowAccent = (r) => {
    if (r.SemDataRenovacao) return { dot: 'bg-[#9CA3AF]', label: 'SEM DATA', cls: 'text-[#6B7280]' };
    if (r.Atingiu) return { dot: 'bg-emerald-500', label: 'OK', cls: 'text-emerald-700' };
    if (r.DaysLeft <= 30) return { dot: 'bg-[#FF2B2B]', label: 'CRÍTICO', cls: 'text-[#FF2B2B]' };
    if (r.DaysLeft <= 90) return { dot: 'bg-amber-500', label: 'ALERTA', cls: 'text-amber-700' };
    return { dot: 'bg-sky-400', label: 'EM CURSO', cls: 'text-sky-700' };
  };
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="downlines-renov-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            Downlines — Renovação de Contrato
          </h3>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Volume anual (Pessoal + Clientes Directos) por ciclo de renovação, para <b>toda a rede descendente</b>. Limiar: <b>{fmt(threshold)}</b> (1º ano: <b>{fmt(data.threshold_1st_year || 7000)}</b>).
          </p>
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">
          {rows.length} downline{rows.length === 1 ? '' : 's'}
        </p>
      </div>
      <div className="overflow-x-auto border border-[#E5E7EB] rounded-md">
        <table className="w-full text-xs">
          <thead className="bg-[#F9FAFB]">
            <tr>
              <th className="text-left  px-2.5 py-2 font-medium text-[#6B7280]">Consultor</th>
              <th className="text-left  px-2.5 py-2 font-medium text-[#6B7280]">Sponsor</th>
              <th className="text-center px-2.5 py-2 font-medium text-[#6B7280]">Data de Validade</th>
              <th className="text-center px-2.5 py-2 font-medium text-[#6B7280]">Dias p/ renovar</th>
              <th className="text-right px-2.5 py-2 font-medium text-[#6B7280]">Total</th>
              <th className="text-right px-2.5 py-2 font-medium text-[#6B7280]">Falta</th>
              <th className="text-center px-2.5 py-2 font-medium text-[#6B7280] min-w-[160px]">Progresso</th>
              <th className="text-center px-2.5 py-2 font-medium text-[#6B7280]">Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const acc = rowAccent(r);
              const barColor = r.Atingiu ? 'bg-emerald-500' : (r.DaysLeft <= 30 ? 'bg-[#FF2B2B]' : (r.DaysLeft <= 90 ? 'bg-amber-500' : 'bg-sky-400'));
              return (
                <tr key={`dlrn-${r.ID_Cliente}`} className="border-t border-[#F3F4F6] hover:bg-[#FAFAF7]" data-testid={`downline-renov-row-${r.ID_Cliente}`}>
                  <td className="px-2.5 py-2"><PersonLink id={r.ID_Cliente} name={r.Nome} />
                    {r.PrimeiroAno && (
                      <span className="ml-1.5 inline-block text-[8px] uppercase tracking-[0.05em] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title={`1º ano de contrato — limiar ${(r.Threshold||7000)} €`}>1º ANO</span>
                    )}
                  </td>
                  <td className="px-2.5 py-2 text-[10px] text-[#6B7280] truncate max-w-[160px]" title={r.Sponsor}>{r.Sponsor || '—'}</td>
                  <td className="px-2.5 py-2 text-center font-mono text-[11px] text-[#374151] whitespace-nowrap">
                    {r.SemDataRenovacao
                      ? <span className="text-[#9CA3AF] italic">sem data registada</span>
                      : fmtDate(r.DataValidade)}
                  </td>
                  <td className={`px-2.5 py-2 text-center font-mono font-semibold ${r.SemDataRenovacao ? 'text-[#9CA3AF]' : acc.cls}`}>
                    {r.SemDataRenovacao ? '—' : r.DaysLeft}
                  </td>
                  <td className="px-2.5 py-2 text-right font-mono font-semibold text-[#111827]">{fmt(r.Total)}</td>
                  <td className={`px-2.5 py-2 text-right font-mono ${r.Atingiu ? 'text-emerald-700' : 'text-[#FF2B2B]'}`}>
                    {r.Atingiu ? '—' : fmt(r.Falta)}
                  </td>
                  <td className="px-2.5 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${r.Pct}%` }} />
                      </div>
                      <span className="text-[10px] font-mono text-[#6B7280] w-10 text-right">{r.Pct.toFixed(0)}%</span>
                    </div>
                  </td>
                  <td className="px-2.5 py-2 text-center">
                    <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.05em] font-semibold ${acc.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${acc.dot}`}></span>
                      {acc.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-[#9CA3AF] italic mt-2">
        Fórmula igual à do card &quot;Volume Faturação Contrato&quot; — exclui downline CS/CT e produtos resell-lead. Filtra por DataPagamento entre as datas do ciclo.
        Estado: <b className="text-emerald-700">OK</b> = atingiu limiar · <b className="text-sky-700">EM CURSO</b> &gt; 90d · <b className="text-amber-700">ALERTA</b> 31-90d · <b className="text-[#FF2B2B]">CRÍTICO</b> ≤ 30d.
      </p>
    </div>
  );
}


function PinDeNegocio({ pinData }) {
  const [showNetworks, setShowNetworks] = useState(false);
  const [showNpts, setShowNpts] = useState(false);
  if (!pinData) return null;
  const { pin, pin_max, pin_max_at, networks_top, downlines = [], ano, mes, top_months_count, current_npts } = pinData;
  const refMes = mes ? `${MONTHS[mes]} ${ano}` : '—';
  const displayPin = pin_max || pin;
  const source = displayPin?.source;
  const isNetworkSource = source === 'networks';
  const isTopSource = source === 'top' || source === 'top_excel';
  const activeTier = isNetworkSource ? displayPin?.pin : null;
  const activeBonus = displayPin?.bonus_pct;
  const maxAtLabel = pin_max_at?.mes ? `${MONTHS[pin_max_at.mes]} ${pin_max_at.ano}` : null;
  const isCurrentMonthDifferent = pin_max && (!pin || pin.pin !== pin_max.pin);

  const sourceDescription = isNetworkSource
    ? 'Baseado em downlines directas a TOP.'
    : source === 'top_excel'
      ? 'Manteve TOP em 3+ meses consecutivos.'
      : source === 'top'
        ? 'Atingiu 20% e ≥1500 NPTS em redes não-TOP.'
        : 'Baseado em NetPoints pessoais.';

  return (
    <section className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="section-pin-negocio">
      <div className="flex items-baseline justify-between mb-3 gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]">Pin de Negócio</h3>
          <p className="text-[11px] text-[#6B7280]">
            Maior pin alguma vez atingido. {sourceDescription}
          </p>
        </div>
        {displayPin ? (
          <div className="flex items-center gap-3" data-testid="pin-current">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Pin Actual</p>
              <p className="text-2xl font-bold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
                {displayPin.nivel}
              </p>
              <p className="text-[11px] text-[#6B7280]">
                {isNetworkSource && (
                  <>
                    <span className="font-mono font-semibold">{displayPin.pin}</span>
                    {' · '}{displayPin.bonus_pct}% · Viagem {displayPin.viagem}
                  </>
                )}
                {isTopSource && (
                  <>
                    <span className="font-mono font-semibold">{displayPin.bonus_pct}%</span>
                    {' · '}{displayPin.months_top} {displayPin.months_top === 1 ? 'mês' : 'meses'} TOP
                  </>
                )}
                {source === 'npts' && (
                  <>
                    <span className="font-mono font-semibold">{displayPin.bonus_pct}%</span>
                    {' · '}{Number(displayPin.netpoints_total).toLocaleString('pt-PT', { maximumFractionDigits: 0 })} NPTS
                  </>
                )}
              </p>
              {maxAtLabel && (
                <p className="text-[10px] text-[#9CA3AF] mt-0.5" data-testid="pin-max-at">
                  {source === 'top_excel' ? 'Confirmado em' : 'Atingido em'} {maxAtLabel}
                </p>
              )}
            </div>
            <div className="bg-[#0F1829] text-white rounded-full w-16 h-16 flex flex-col items-center justify-center" data-testid="pin-badge">
              {isNetworkSource ? (
                <>
                  <span className="text-xl font-bold leading-none">{displayPin.networks_top}</span>
                  <span className="text-[8px] uppercase tracking-wider opacity-80">Networks</span>
                </>
              ) : isTopSource ? (
                <>
                  <span className="text-xl font-bold leading-none">{top_months_count || 0}</span>
                  <span className="text-[8px] uppercase tracking-wider opacity-80">Meses TOP</span>
                </>
              ) : (
                <>
                  <span className="text-xl font-bold leading-none">{displayPin.bonus_pct}%</span>
                  <span className="text-[8px] uppercase tracking-wider opacity-80">Bónus</span>
                </>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#6B7280] italic">Ainda sem pin atribuído.</p>
        )}
      </div>

      {isCurrentMonthDifferent && (
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-md px-3 py-2 mb-3 text-[11px]" data-testid="pin-current-month-note">
          <span className="text-[#6B7280]">No mês {refMes}: </span>
          {pin ? (
            <span className="font-medium text-[#111827]">
              <span className="font-mono">{pin.pin}</span> · {pin.nivel} · {pin.bonus_pct}% · {networks_top} {networks_top === 1 ? 'network' : 'networks'} TOP
            </span>
          ) : (
            <span className="font-medium text-[#6B7280] italic">sem networks TOP ({networks_top})</span>
          )}
        </div>
      )}

      {current_npts && (
        <div className="mb-3">
          <NextPinHint
            npts={current_npts.netpoints_total}
            mesLabel={current_npts.mes ? `${MONTHS[current_npts.mes]} ${current_npts.ano}` : null}
          />
        </div>
      )}

      <PinHistoryStrip history={pinData.pin_history || []} />

      <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-2">
        Downlines directas — {refMes} ({downlines.length})
      </p>
      <div className="border border-[#E5E7EB] rounded-md overflow-hidden mb-3">
        <table className="w-full text-xs">
          <thead className="bg-[#F9FAFB]">
            <tr>
              <th className="text-left px-2.5 py-1.5 font-medium text-[#6B7280]">Nome</th>
              <th className="text-right px-2.5 py-1.5 font-medium text-[#6B7280]">NetPoints</th>
              <th className="text-center px-2.5 py-1.5 font-medium text-[#6B7280]">Bónus</th>
            </tr>
          </thead>
          <tbody>
            {downlines.length === 0 && (
              <tr><td colSpan={3} className="text-center py-3 text-[#9CA3AF] italic">Sem downlines directas</td></tr>
            )}
            {downlines.map(d => {
              const isTop = Number(d.Bonus) >= 20;
              return (
                <tr key={`pn-${d.ID_Cliente}`} className="border-t border-[#F3F4F6]">
                  <td className="px-2.5 py-1.5">
                    <PersonLink id={d.ID_Cliente} name={d.NomeCompleto} />
                  </td>
                  <td className="px-2.5 py-1.5 text-right font-mono">{Number(d.NetPointsTotal).toLocaleString('pt-PT', {maximumFractionDigits: 2})}</td>
                  <td className="px-2.5 py-1.5 text-center">
                    <span className={`inline-block text-[9px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded border ${isTop ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]'}`}>
                      {isTop ? 'TOP' : `${Number(d.Bonus)}%`}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Toggles para escalas (compactos, lado a lado) */}
      <div className="flex flex-wrap items-center gap-3 text-[11px]">
        <button
          type="button"
          onClick={() => setShowNpts(s => !s)}
          data-testid="toggle-npts-table"
          className="text-[#0F1829] hover:underline inline-flex items-center gap-1"
        >
          <span className="text-[9px]">{showNpts ? '▾' : '▸'}</span>
          Escala NetPoints pessoais (0% → 20%) · TOP · TOP EXCEL
        </button>
        <button
          type="button"
          onClick={() => setShowNetworks(s => !s)}
          data-testid="toggle-networks-table"
          className="text-[#0F1829] hover:underline inline-flex items-center gap-1"
        >
          <span className="text-[9px]">{showNetworks ? '▾' : '▸'}</span>
          Escala Networks TOP (Silver → Vice-President)
        </button>
      </div>

      {showNpts && (
        <div className="mt-3 border border-[#E5E7EB] rounded-md overflow-hidden" data-testid="npts-table">
          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB]">
              <tr>
                <th className="text-left px-2.5 py-1.5 font-medium text-[#6B7280]">NetPoints / Condição</th>
                <th className="text-right px-2.5 py-1.5 font-medium text-[#6B7280]">Bónus</th>
                <th className="text-left px-2.5 py-1.5 font-medium text-[#6B7280]">Designação</th>
              </tr>
            </thead>
            <tbody>
              <tr className={`border-t border-[#F3F4F6] ${source === 'top_excel' ? 'bg-amber-50 font-semibold' : ''}`}>
                <td className="px-2.5 py-1.5 italic text-[#374151]">TOP em 3 meses consecutivos</td>
                <td className="px-2.5 py-1.5 text-right font-mono">20%</td>
                <td className="px-2.5 py-1.5">TOP EXCEL</td>
              </tr>
              <tr className={`border-t border-[#F3F4F6] ${source === 'top' ? 'bg-amber-50 font-semibold' : ''}`}>
                <td className="px-2.5 py-1.5 italic text-[#374151]">20% + ≥1500 NPTS em redes não-TOP</td>
                <td className="px-2.5 py-1.5 text-right font-mono">20%</td>
                <td className="px-2.5 py-1.5">TOP</td>
              </tr>
              {NPTS_TABLE.map(t => {
                const isActive = source === 'npts' && activeBonus === t.bonus;
                return (
                  <tr key={t.nivel} className={`border-t border-[#F3F4F6] ${isActive ? 'bg-amber-50 font-semibold' : ''}`}>
                    <td className="px-2.5 py-1.5 font-mono">{t.range}</td>
                    <td className="px-2.5 py-1.5 text-right font-mono">{t.bonus}%</td>
                    <td className="px-2.5 py-1.5">{t.nivel}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showNetworks && (
        <div className="mt-3 border border-[#E5E7EB] rounded-md overflow-hidden" data-testid="networks-table">
          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB]">
              <tr>
                <th className="text-right px-2.5 py-1.5 font-medium text-[#6B7280]">Networks</th>
                <th className="text-right px-2.5 py-1.5 font-medium text-[#6B7280]">Bónus</th>
                <th className="text-left px-2.5 py-1.5 font-medium text-[#6B7280]">Nível</th>
                <th className="text-left px-2.5 py-1.5 font-medium text-[#6B7280]">Pin</th>
                <th className="text-left px-2.5 py-1.5 font-medium text-[#6B7280]">Viagem</th>
              </tr>
            </thead>
            <tbody>
              {PIN_TABLE.map(t => {
                const isActive = activeTier === t.pin;
                return (
                  <tr key={t.pin} data-testid={`pin-row-${t.pin}`} className={`border-t border-[#F3F4F6] ${isActive ? 'bg-amber-50 font-semibold' : ''}`}>
                    <td className="px-2.5 py-1.5 text-right font-mono">{t.min === 15 ? '≥15' : t.min}</td>
                    <td className="px-2.5 py-1.5 text-right font-mono">{t.bonus}%</td>
                    <td className="px-2.5 py-1.5">{t.nivel}</td>
                    <td className="px-2.5 py-1.5 font-mono">{t.pin}</td>
                    <td className="px-2.5 py-1.5 text-[#6B7280]">{t.viagem}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AgeGenderChart({ ageDist, genderAge, genderDist }) {
  if (!ageDist) return null;
  const buckets = ageDist.buckets || [];
  const comData = ageDist.comData || 0;
  const semData = ageDist.semData || 0;
  if (comData === 0) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="age-gender-empty">
        <h3 className="text-sm font-semibold text-[#111827] mb-1" style={{ fontFamily: "'Fraunces', sans-serif" }}>Distribuição Etária × Género</h3>
        <p className="text-sm text-[#6B7280]">Sem clientes na rede com data de nascimento registada.</p>
      </div>
    );
  }
  const genByFaixa = {};
  (genderAge?.buckets || []).forEach(b => { genByFaixa[b.faixa] = b; });
  const chart = buckets.map(b => {
    const g = genByFaixa[b.faixa] || {};
    const F = g.feminino || 0;
    const M = g.masculino || 0;
    const ND = Math.max(0, b.quantidade - F - M);
    return { faixa: b.faixa, total: b.quantidade, F, M, ND, pct: comData > 0 ? (b.quantidade / comData) * 100 : 0 };
  });
  // Resumo GLOBAL de género da rede (todos os clientes, independente da idade)
  const gFem = genderDist?.feminino || 0;
  const gMasc = genderDist?.masculino || 0;
  const gCom = genderDist?.comInfo || (gFem + gMasc);
  const gPctF = gCom > 0 ? (gFem / gCom) * 100 : 0;
  const gPctM = gCom > 0 ? (gMasc / gCom) * 100 : 0;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="age-gender-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            Distribuição Etária × Género
          </h3>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Clientes da rede por intervalo de idade, com o peso de mulheres, homens e não definido (ND).
          </p>
        </div>
        <div className="flex items-center gap-5">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Idade Média</p>
            <p className="text-2xl font-bold font-mono text-[#0F1829]">{ageDist.mediaIdade != null ? ageDist.mediaIdade : '—'}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Com Data</p>
            <p className="text-2xl font-bold font-mono text-[#111827]">{comData.toLocaleString('pt-PT')}</p>
          </div>
        </div>
      </div>
      {gCom > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-3" data-testid="age-gender-summary">
          <div className="rounded-lg border border-[#F3D6E4] bg-[#FDF2F8] px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#BE185D]">Mulheres</p>
            <p className="text-lg font-bold font-mono text-[#DB2777]">{gFem.toLocaleString('pt-PT')} <span className="text-xs font-normal text-[#9D174D]">({gPctF.toFixed(1)}%)</span></p>
          </div>
          <div className="rounded-lg border border-[#CFE0F5] bg-[#EFF6FF] px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#1D4ED8]">Homens</p>
            <p className="text-lg font-bold font-mono text-[#2563EB]">{gMasc.toLocaleString('pt-PT')} <span className="text-xs font-normal text-[#1E40AF]">({gPctM.toFixed(1)}%)</span></p>
          </div>
          <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">Com Género</p>
            <p className="text-lg font-bold font-mono text-[#111827]">{gCom.toLocaleString('pt-PT')}</p>
          </div>
        </div>
      )}
      <div className="flex items-center gap-4 text-xs mb-2">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#DB2777' }} />Mulheres</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#2563EB' }} />Homens</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-[#D1D5DB]" />ND (sem género)</span>
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="faixa" tick={{ fontSize: 11, fill: '#6B7280' }} />
            <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} allowDecimals={false} />
            <Tooltip
              formatter={(v, n) => [v.toLocaleString('pt-PT'), n === 'F' ? 'Mulheres' : (n === 'M' ? 'Homens' : 'ND (sem género)')]}
              labelFormatter={(l) => { const b = chart.find(x => x.faixa === l); return b ? `${l} anos · ${b.total.toLocaleString('pt-PT')} clientes (${b.pct.toFixed(1)}%)` : l; }}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
            <Bar dataKey="F" stackId="a" fill="#DB2777" />
            <Bar dataKey="M" stackId="a" fill="#2563EB" />
            <Bar dataKey="ND" stackId="a" fill="#D1D5DB" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-[#9CA3AF] italic mt-2" data-testid="age-gender-note">
        Cartões de género = toda a rede ({gCom.toLocaleString('pt-PT')} com género). Barras = clientes com data de nascimento válida ({comData.toLocaleString('pt-PT')}); <b>ND</b> = com idade mas sem género.
        {semData > 0 && <> {semData.toLocaleString('pt-PT')} sem data de nascimento (ex.: 01/01/1900) foram ignorados nas barras.</>}
      </p>
    </div>
  );
}

function GeoDistribution({ data, genderDistrict }) {
  const [view, setView] = useState('distrito');
  if (!data) return null;
  const distritos = data.distritos || [];
  const cidades = data.cidades || [];
  const paises = data.paises || [];
  const comMorada = data.comMorada || 0;
  const semMorada = data.semMorada || 0;

  if (comMorada === 0) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="geo-distribution-empty">
        <h3 className="text-sm font-semibold text-[#111827] mb-1" style={{ fontFamily: "'Fraunces', sans-serif" }}>Distribuição Geográfica da Rede</h3>
        <p className="text-sm text-[#6B7280]">Sem clientes na rede com morada registada.</p>
      </div>
    );
  }

  // País: top 12 + Outros (com nº de países agregados)
  let paisChart = paises.map(p => ({ nome: p.pais, quantidade: p.quantidade, feminino: p.feminino || 0, masculino: p.masculino || 0 }));
  if (paisChart.length > 12) {
    const top = paisChart.slice(0, 12);
    const resto = paisChart.slice(12);
    const outros = resto.reduce((s, p) => s + p.quantidade, 0);
    const outrosF = resto.reduce((s, p) => s + p.feminino, 0);
    const outrosM = resto.reduce((s, p) => s + p.masculino, 0);
    paisChart = [...top, { nome: `Outros (${resto.length} países)`, quantidade: outros, feminino: outrosF, masculino: outrosM }];
  }

  // F/M/ND disponível em todas as vistas (a partir dos próprios dados)
  const withGender = (label, quantidade, feminino, masculino) => {
    const F = feminino || 0, M = masculino || 0;
    return { label, quantidade, F, M, ND: Math.max(0, quantidade - F - M) };
  };
  const hasGender = true;

  const config = {
    distrito: {
      rows: distritos.map(d => withGender(d.distrito, d.quantidade, d.feminino, d.masculino)),
      note: 'Distrito estimado pelo código postal (morada Residência › Faturação › Patronal). ND = sem género registado.',
    },
    cidade: {
      rows: cidades.map(c => withGender(c.cidade, c.quantidade, c.feminino, c.masculino)),
      note: 'Top 15 cidades (Localidade da morada). ND = sem género registado.',
    },
    pais: {
      rows: paisChart.map(p => withGender(p.nome, p.quantidade, p.feminino, p.masculino)),
      note: `${paises.length} ${paises.length === 1 ? 'país representado' : 'países representados'} na rede. ND = sem género registado.`,
    },
  };
  const cur = config[view];
  const chartHeight = Math.max(200, cur.rows.length * 26);

  const TABS = [
    { key: 'distrito', label: 'Distrito' },
    { key: 'cidade', label: 'Cidade' },
    { key: 'pais', label: 'País' },
  ];

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="geo-distribution-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            Distribuição Geográfica da Rede
          </h3>
          <p className="text-xs text-[#6B7280] mt-0.5">Onde está concentrada a rede descendente, com o peso de mulheres/homens.</p>
        </div>
        <div className="flex items-center gap-1 bg-[#F3F4F6] rounded-md p-0.5" data-testid="geo-view-toggle">
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              data-testid={`geo-view-${t.key}`}
              onClick={() => setView(t.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${view === t.key ? 'bg-white text-[#0F1829] shadow-sm' : 'text-[#6B7280] hover:text-[#374151]'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {hasGender && (
        <div className="flex items-center gap-4 text-xs mb-2" data-testid="geo-gender-legend">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#DB2777' }} />Mulheres</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#2563EB' }} />Homens</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-[#D1D5DB]" />ND</span>
        </div>
      )}
      <div style={{ height: `${chartHeight}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={cur.rows} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
            <XAxis type="number" tick={{ fontSize: 10, fill: '#6B7280' }} allowDecimals={false} />
            <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11, fill: '#374151' }} />
            <Tooltip
              formatter={(v, n) => {
                const lbl = n === 'F' ? 'Mulheres' : (n === 'M' ? 'Homens' : (n === 'ND' ? 'ND (sem género)' : 'Clientes'));
                return [v.toLocaleString('pt-PT'), lbl];
              }}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
            {hasGender ? (
              <>
                <Bar dataKey="F" stackId="g" fill="#DB2777" />
                <Bar dataKey="M" stackId="g" fill="#2563EB" />
                <Bar dataKey="ND" stackId="g" fill="#D1D5DB" radius={[0, 4, 4, 0]}
                  label={{ position: 'right', fontSize: 10, fill: '#6B7280', formatter: (v, e) => (e && e.payload ? e.payload.quantidade.toLocaleString('pt-PT') : '') }} />
              </>
            ) : (
              <Bar dataKey="quantidade" radius={[0, 4, 4, 0]} fill="#4F6DC7" label={{ position: 'right', fontSize: 10, fill: '#6B7280', formatter: (v) => v.toLocaleString('pt-PT') }} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-[#9CA3AF] italic mt-2" data-testid="geo-distribution-note">
        {cur.note} Base: {comMorada.toLocaleString('pt-PT')} clientes com morada
        {semMorada > 0 && <> · {semMorada.toLocaleString('pt-PT')} sem morada registada</>}.
      </p>
    </div>
  );
}

function AgeVolume({ data, fmt }) {
  if (!data) return null;
  const buckets = data.buckets || [];
  const totalVolume = data.totalVolume || 0;
  if (totalVolume === 0) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="age-volume-empty">
        <h3 className="text-sm font-semibold text-[#111827] mb-1" style={{ fontFamily: "'Fraunces', sans-serif" }}>Faturação por Faixa Etária</h3>
        <p className="text-sm text-[#6B7280]">Sem compras registadas para clientes com data de nascimento válida.</p>
      </div>
    );
  }
  const chart = buckets.map(b => ({
    faixa: b.faixa,
    volume: b.volume,
    compradores: b.compradores,
    media: b.mediaPorComprador,
    pct: totalVolume > 0 ? (b.volume / totalVolume) * 100 : 0,
    volF: b.volumeFeminino || 0,
    volM: b.volumeMasculino || 0,
    volSem: Math.max(0, (b.volume || 0) - (b.volumeFeminino || 0) - (b.volumeMasculino || 0)),
    pctF: b.pctFeminino || 0,
    pctM: b.pctMasculino || 0,
  }));
  const maxBucket = chart.reduce((m, b) => (b.volume > m.volume ? b : m), chart[0]);
  const totalVF = data.totalVolumeFeminino || 0;
  const totalVM = data.totalVolumeMasculino || 0;
  const totalGen = totalVF + totalVM;
  const pctVF = totalGen > 0 ? (totalVF / totalGen) * 100 : 0;
  const pctVM = totalGen > 0 ? (totalVM / totalGen) * 100 : 0;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="age-volume-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            Faturação por Faixa Etária
          </h3>
          <p className="text-xs text-[#6B7280] mt-0.5">Volume de compras da rede cruzado com a idade — que faixa gera mais faturação.</p>
        </div>
        <div className="flex items-center gap-5">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Volume Total</p>
            <p className="text-xl font-bold font-mono text-[#0F1829]">{fmt(totalVolume)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Faixa Líder</p>
            <p className="text-xl font-bold font-mono text-[#FF6B2B]">{maxBucket.faixa}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Peso M/H</p>
            <p className="text-xl font-bold font-mono">
              <span className="text-[#DB2777]">{pctVF.toFixed(0)}%</span>
              <span className="text-[#9CA3AF]"> / </span>
              <span className="text-[#2563EB]">{pctVM.toFixed(0)}%</span>
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs mb-2">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#DB2777' }} />Mulheres <b className="font-mono">{fmt(totalVF)}</b></span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#2563EB' }} />Homens <b className="font-mono">{fmt(totalVM)}</b></span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-[#D1D5DB]" />Sem género</span>
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart} margin={{ top: 16, right: 8, left: 8, bottom: 0 }}>
            <XAxis dataKey="faixa" tick={{ fontSize: 11, fill: '#6B7280' }} />
            <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
            <Tooltip
              formatter={(v, n) => [fmt(v), n === 'volF' ? 'Mulheres' : (n === 'volM' ? 'Homens' : 'Sem género')]}
              labelFormatter={(l) => {
                const b = chart.find(x => x.faixa === l);
                return b ? `${l} anos · ${fmt(b.volume)} (${b.pct.toFixed(1)}%) · M ${b.pctF.toFixed(0)}% / H ${b.pctM.toFixed(0)}%` : l;
              }}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
            <Bar dataKey="volF" stackId="g" fill="#DB2777" />
            <Bar dataKey="volM" stackId="g" fill="#2563EB" />
            <Bar dataKey="volSem" stackId="g" fill="#D1D5DB" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto mt-3 border border-[#E5E7EB] rounded-md">
        <table className="w-full text-xs">
          <thead className="bg-[#F9FAFB]">
            <tr>
              <th className="text-left px-2.5 py-1.5 font-medium text-[#6B7280]">Faixa</th>
              <th className="text-right px-2.5 py-1.5 font-medium text-[#6B7280]">Volume</th>
              <th className="text-right px-2.5 py-1.5 font-medium text-[#6B7280]">% Total</th>
              <th className="text-right px-2.5 py-1.5 font-medium text-[#DB2777]">Mulheres</th>
              <th className="text-right px-2.5 py-1.5 font-medium text-[#2563EB]">Homens</th>
              <th className="text-right px-2.5 py-1.5 font-medium text-[#6B7280]">Compradores</th>
            </tr>
          </thead>
          <tbody>
            {chart.map(b => (
              <tr key={`avr-${b.faixa}`} className={`border-t border-[#F3F4F6] ${b.faixa === maxBucket.faixa ? 'bg-orange-50 font-semibold' : ''}`} data-testid={`age-volume-row-${b.faixa}`}>
                <td className="px-2.5 py-1.5">{b.faixa}</td>
                <td className="px-2.5 py-1.5 text-right font-mono">{fmt(b.volume)}</td>
                <td className="px-2.5 py-1.5 text-right font-mono text-[#6B7280]">{b.pct.toFixed(1)}%</td>
                <td className="px-2.5 py-1.5 text-right font-mono text-[#DB2777]">{fmt(b.volF)} <span className="text-[10px] text-[#9CA3AF]">({b.pctF.toFixed(0)}%)</span></td>
                <td className="px-2.5 py-1.5 text-right font-mono text-[#2563EB]">{fmt(b.volM)} <span className="text-[10px] text-[#9CA3AF]">({b.pctM.toFixed(0)}%)</span></td>
                <td className="px-2.5 py-1.5 text-right font-mono">{b.compradores.toLocaleString('pt-PT')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-[#9CA3AF] italic mt-2" data-testid="age-volume-note">
        Encomendas pagas/encerradas. Só clientes com data de nascimento válida (01/01/1900 ignorado). Percentagens M/H sobre a faturação com género identificado; "sem género" refere-se a quem não tem sexo registado.
      </p>
    </div>
  );
}

// ── Cores por faixa etária ──────────────────────────────────────────
const FAIXA_COLORS = {
  '< 18': '#94A3B8', '18-24': '#38BDF8', '25-34': '#22C55E', '35-44': '#EAB308',
  '45-54': '#F97316', '55-64': '#A855F7', '65+': '#EF4444',
};

function AgeDistrictCross({ data, geoDistritos }) {
  if (!data || !(data.distritos || []).length) return null;
  const faixas = data.faixas || [];
  const distritos = data.distritos || [];
  const geoByName = {};
  (geoDistritos || []).forEach(d => { geoByName[d.distrito] = d; });
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="age-district-cross-card">
      <h3 className="text-sm font-semibold text-[#111827] mb-1" style={{ fontFamily: "'Fraunces', sans-serif" }}>
        Faixa Etária por Distrito
      </h3>
      <p className="text-xs text-[#6B7280] mb-3">Que faixa etária domina em cada distrito — para adaptar a abordagem por zona.</p>
      <div className="flex items-center gap-4 text-[10px] mb-2 text-[#6B7280]">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#DB2777' }} />Mulheres</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#2563EB' }} />Homens</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-[#D1D5DB]" />ND</span>
      </div>
      <div className="overflow-x-auto border border-[#E5E7EB] rounded-md">
        <table className="w-full text-xs">
          <thead className="bg-[#F9FAFB]">
            <tr>
              <th className="text-left px-2.5 py-2 font-medium text-[#6B7280]">Distrito</th>
              <th className="text-right px-2.5 py-2 font-medium text-[#6B7280]">Total</th>
              {faixas.map(f => <th key={`h-${f}`} className="text-right px-2 py-2 font-medium text-[#6B7280]">{f}</th>)}
              <th className="text-left px-2.5 py-2 font-medium text-[#6B7280]">Dominante</th>
              <th className="text-left px-2.5 py-2 font-medium text-[#6B7280] min-w-[130px]">Género (M/H/ND)</th>
            </tr>
          </thead>
          <tbody>
            {distritos.map(d => {
              const byFaixa = {};
              d.faixas.forEach(x => { byFaixa[x.faixa] = x.quantidade; });
              const geo = geoByName[d.distrito];
              let genderCell = <span className="text-[10px] text-[#9CA3AF]">—</span>;
              if (geo && geo.quantidade > 0) {
                const F = geo.feminino || 0, M = geo.masculino || 0, ND = Math.max(0, geo.quantidade - F - M);
                const t = geo.quantidade;
                genderCell = (
                  <div>
                    <div className="flex h-2 rounded-full overflow-hidden bg-[#F3F4F6]" title={`Mulheres ${F} · Homens ${M} · ND ${ND}`}>
                      <div className="bg-[#DB2777]" style={{ width: `${(F / t) * 100}%` }} />
                      <div className="bg-[#2563EB]" style={{ width: `${(M / t) * 100}%` }} />
                      <div className="bg-[#D1D5DB]" style={{ width: `${(ND / t) * 100}%` }} />
                    </div>
                    <span className="text-[9px] font-mono text-[#9CA3AF]">{F}/{M}/{ND}</span>
                  </div>
                );
              }
              return (
                <tr key={`adc-${d.distrito}`} className="border-t border-[#F3F4F6] hover:bg-[#FAFAF7]" data-testid={`age-district-row-${d.distrito}`}>
                  <td className="px-2.5 py-1.5 font-medium text-[#111827]">{d.distrito}</td>
                  <td className="px-2.5 py-1.5 text-right font-mono text-[#374151]">{d.total.toLocaleString('pt-PT')}</td>
                  {faixas.map(f => {
                    const v = byFaixa[f] || 0;
                    const isDom = f === d.faixaDominante;
                    return (
                      <td key={`c-${d.distrito}-${f}`} className={`px-2 py-1.5 text-right font-mono ${isDom ? 'font-bold' : 'text-[#9CA3AF]'}`}
                        style={isDom ? { color: FAIXA_COLORS[f] } : undefined}>
                        {v || '—'}
                      </td>
                    );
                  })}
                  <td className="px-2.5 py-1.5">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: `${FAIXA_COLORS[d.faixaDominante]}22`, color: FAIXA_COLORS[d.faixaDominante] }}>
                      {d.faixaDominante} · {d.faixaDominantePct}%
                    </span>
                  </td>
                  <td className="px-2.5 py-1.5">{genderCell}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-[#9CA3AF] italic mt-2">
        Colunas de faixa etária = clientes com <b>data de nascimento válida</b> (total pode ser inferior ao gráfico geográfico). A coluna <b>Género</b> reflete todos os clientes com morada no distrito (F/M/ND, ND = sem género).
      </p>
    </div>
  );
}

export default function IndicatorsPage() {
  const [loading, setLoading] = useState(true);
  const [topBuyers, setTopBuyers] = useState([]);
  const [bottomBuyers, setBottomBuyers] = useState([]);
  const [orderGraph, setOrderGraph] = useState([]);
  const [clientType, setClientType] = useState([]);
  const [noOrders, setNoOrders] = useState(null);
  const [orders1yr, setOrders1yr] = useState(null);
  const [unconfirmed, setUnconfirmed] = useState({ total: 0, data: [] });
  const [consultorPin, setConsultorPin] = useState(null);
  const [downlinesRenovacao, setDownlinesRenovacao] = useState(null);
  const [ageDist, setAgeDist] = useState(null);
  const [geoDist, setGeoDist] = useState(null);
  const [ageVolume, setAgeVolume] = useState(null);
  const [geoAgeCross, setGeoAgeCross] = useState(null);
  const [genderDist, setGenderDist] = useState(null);
  const [genderAge, setGenderAge] = useState(null);
  const [tab, setTab] = useState('top');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  useEffect(() => {
    Promise.all([
      api.post('/indicadores/topBuyers').then(r => setTopBuyers(r.data?.clientes || [])).catch(() => {}),
      api.post('/indicadores/bottomBuyers').then(r => setBottomBuyers(r.data?.clientes || [])).catch(() => {}),
      api.post('/indicadores/orderRedeGraph').then(r => setOrderGraph(r.data?.data || [])).catch(() => {}),
      api.post('/indicadores/clientType').then(r => setClientType(Array.isArray(r.data) ? r.data : [])).catch(() => {}),
      api.post('/indicadores/clientsWithNoOrders').then(r => { const c = r.data?.clientes; setNoOrders(Array.isArray(c) && c[0] ? c[0].numero : 0); }).catch(() => {}),
      api.post('/indicadores/clientsWithOrders1YearAgo').then(r => { const c = r.data?.clientes; setOrders1yr(Array.isArray(c) && c[0] ? c[0].numero : 0); }).catch(() => {}),
      api.post('/indicadores/unconfirmedRegistrations').then(r => setUnconfirmed(r.data || { total: 0, data: [] })).catch(() => {}),
      api.post('/indicadores/consultorPin', {}).then(r => setConsultorPin(r.data || null)).catch(() => {}),
      api.post('/dashboard/downlinesContratoVolume', {}).then(r => setDownlinesRenovacao(r.data || null)).catch(() => {}),
      api.post('/indicadores/ageDistribution', {}).then(r => setAgeDist(r.data || null)).catch(() => {}),
      api.post('/indicadores/geoDistribution', {}).then(r => setGeoDist(r.data || null)).catch(() => {}),
      api.post('/indicadores/ageVolume', {}).then(r => setAgeVolume(r.data || null)).catch(() => {}),
      api.post('/indicadores/geoAgeCross', {}).then(r => setGeoAgeCross(r.data || null)).catch(() => {}),
      api.post('/indicadores/genderDistribution', {}).then(r => setGenderDist(r.data || null)).catch(() => {}),
      api.post('/indicadores/genderAgeCross', {}).then(r => setGenderAge(r.data || null)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  const chartData = orderGraph.map(g => ({ name: `${MONTHS[g.mes]} ${g.ano}`, valor: g.valorEncomendas || 0 }));

  const filteredUnconfirmed = (unconfirmed.data || []).filter(r => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.NomeCompleto || '').toLowerCase().includes(q) || (r.Email || '').toLowerCase().includes(q) || (r.NomeConsultor || '').toLowerCase().includes(q);
  });
  const PAGE_SIZE = 25;
  const pagedUnconfirmed = filteredUnconfirmed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filteredUnconfirmed.length / PAGE_SIZE));

  return (
    <div data-testid="indicators-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Indicadores</h1>
        <p className="text-sm text-[#6B7280] mt-1">KPIs e indicadores de performance da rede</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Clientes Sem Encomenda</p>
          <p className="text-2xl font-bold font-mono text-[#111827] mt-1">{noOrders ?? '-'}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Sem Enc. Há 1 Ano</p>
          <p className="text-2xl font-bold font-mono text-[#111827] mt-1">{orders1yr ?? '-'}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#FF2B2B]">Registo Não Confirmado</p>
          <p className="text-2xl font-bold font-mono text-[#FF2B2B] mt-1">{unconfirmed.total}</p>
        </div>
        {clientType.map((ct) => (
          <div key={`ct-${ct.tipo}`} className="bg-white border border-[#E5E7EB] rounded-lg p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">{ct.tipo}</p>
            <p className="text-2xl font-bold font-mono text-[#111827] mt-1">{ct.quantidade}</p>
          </div>
        ))}
      </div>

      {/* Pin de Negócio */}
      <PinDeNegocio pinData={consultorPin} />

      {/* Downlines — Renovação de Contrato */}
      <DownlinesRenovacao data={downlinesRenovacao} fmt={fmt} />

      {/* Distribuição Etária × Género (F/M/ND) + resumo global de género */}
      <AgeGenderChart ageDist={ageDist} genderAge={genderAge} genderDist={genderDist} />

      {/* Faturação por Faixa Etária */}
      <AgeVolume data={ageVolume} fmt={fmt} />

      {/* Distribuição Geográfica da Rede (todas as vistas com género F/M/ND) */}
      <GeoDistribution data={geoDist} />

      {/* Faixa Etária por Distrito */}
      <AgeDistrictCross data={geoAgeCross} geoDistritos={geoDist?.distritos} />

      {/* Order Graph */}
      {chartData.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
          <h3 className="text-sm font-semibold text-[#111827] mb-4">Volume Encomendas Rede (12 Meses)</h3>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                <Bar dataKey="valor" fill="#0F1829" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E5E7EB]">
        {[{key:'top',label:'Melhores Compradores'},{key:'bottom',label:'Piores Compradores'},{key:'unconfirmed',label:'Registo Não Confirmado'}].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setPage(1); setSearch(''); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab===t.key?'border-[#0F1829] text-[#0F1829]':'border-transparent text-[#6B7280]'}`}>
            {t.label} {t.key === 'unconfirmed' && unconfirmed.total > 0 && <span className="ml-1 text-[10px] font-bold text-white bg-[#FF2B2B] rounded-full px-1.5 py-0.5">{unconfirmed.total}</span>}
          </button>
        ))}
      </div>

      {/* Search */}
      {tab === 'unconfirmed' && (
        <div className="mb-4 relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full sm:w-80 pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none"
            placeholder="Pesquisar nome, email ou consultor..." />
          {search && <span className="ml-3 text-xs text-[#6B7280]">{filteredUnconfirmed.length} resultado(s)</span>}
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">ID</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Nome</th>
            {tab !== 'unconfirmed' && <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Total Compras</th>}
            {tab === 'unconfirmed' && <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Email</th>}
            {tab === 'unconfirmed' && <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Consultor</th>}
            {tab === 'unconfirmed' && <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Data Registo</th>}
          </tr></thead>
          <tbody>
            {tab !== 'unconfirmed' && (tab === 'top' ? topBuyers : bottomBuyers).slice(0, 30).map((c) => (
              <tr key={`buyer-${c.ID_Cliente}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                <td className="px-4 py-3 text-sm font-mono text-[#6B7280]">{c.ID_Cliente}</td>
                <td className="px-4 py-3 text-sm font-medium text-[#111827]"><PersonLink id={c.ID_Cliente} name={c.NomeCompleto} /></td>
                <td className="px-4 py-3 text-sm font-mono text-right text-[#111827]">{fmt(c.totalCompras)}</td>
              </tr>
            ))}
            {tab === 'unconfirmed' && pagedUnconfirmed.map((c) => (
              <tr key={`unc-${c.ID_Cliente}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                <td className="px-4 py-3 text-sm font-mono text-[#6B7280]">{c.ID_Cliente}</td>
                <td className="px-4 py-3 text-sm font-medium text-[#111827]"><PersonLink id={c.ID_Cliente} name={c.NomeCompleto} /></td>
                <td className="px-4 py-3 text-sm text-[#6B7280]">{c.Email}</td>
                <td className="px-4 py-3 text-sm">{c.ID_Consultor ? <PersonLink id={c.ID_Consultor} name={c.NomeConsultor} /> : '-'}</td>
                <td className="px-4 py-3 text-sm text-[#6B7280]">{c.DataRegisto}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {tab !== 'unconfirmed' && (tab === 'top' ? topBuyers : bottomBuyers).length === 0 && <div className="text-center py-8 text-[#6B7280]">Sem dados</div>}
        {tab === 'unconfirmed' && pagedUnconfirmed.length === 0 && <div className="text-center py-8 text-[#6B7280]">Sem dados</div>}
        {tab === 'unconfirmed' && totalPages > 1 && (
          <div className="flex flex-col items-center gap-2 px-4 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
            <p className="text-xs text-[#6B7280]">Página {page} de {totalPages} ({filteredUnconfirmed.length} registos)</p>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                <CaretLeft size={12} /> Anterior
              </button>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                Seguinte <CaretRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
